import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, ShieldCheckIcon, RefreshCwIcon, DatabaseIcon, GaugeIcon, DownloadCloudIcon, ActivityIcon, BugIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function WordPressOnderhoudPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const scrollToSection = (e: React.MouseEvent, sectionId: string) => {
    e.preventDefault();
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'WordPress onderhoud | Veilig & up-to-date | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'WordPress onderhoud: houd je WordPress website veilig, snel en up-to-date. Professioneel onderhoud door Noord Digital.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'WordPress onderhoud | Veilig & up-to-date | Noord',
      'og:description': 'WordPress onderhoud: houd je WordPress website veilig, snel en up-to-date. Professioneel onderhoud door Noord Digital.',
      'og:type': 'article',
      'og:url': 'https://noorddigital.nl/wordpress-onderhoud',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['pakketten', 'contact'];
      sections.forEach(sectionId => {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            const anchor = document.getElementById('anchor');
            if (anchor) {
              anchor.href = `#${sectionId}`;
            }
          }
        }
      });
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const faqs = [{
    q: 'Hoe vaak worden WordPress updates uitgevoerd?',
    a: "Wij voeren wekelijks controles uit op beschikbare updates voor WordPress core, plugins en thema's. Kritieke beveiligingsupdates worden direct doorgevoerd. Reguliere updates plannen wij zorgvuldig in om compatibiliteitsproblemen te voorkomen."
  }, {
    q: 'Wat gebeurt er als er een probleem optreedt?',
    a: 'Bij problemen grijpen wij direct in. Dankzij onze dagelijkse backups kunnen we jouw website snel herstellen naar een werkende versie. Daarnaast monitoren wij je website 24/7, zodat we problemen vaak al oplossen voordat jij ze merkt.'
  }, {
    q: 'Heb ik WordPress onderhoud echt nodig?',
    a: 'Ja. WordPress is open-source software die regelmatig updates ontvangt voor beveiliging en functionaliteit. Zonder onderhoud loop je risico op beveiligingslekken, trage laadtijden en compatibiliteitsproblemen met plugins. Professioneel WordPress onderhoud voorkomt deze problemen.'
  }];
  return <main className="pt-16 lg:pt-[72px]" data-id="element-2924">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-2925">
        <div className="absolute inset-0 pointer-events-none" data-id="element-2926">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-2927" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-2928" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-2929" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2930">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-2931">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-2932">
              Home
            </a>
            <span className="text-navy-500" data-id="element-2933">/</span>
            <a href="#/kennisbank" className="text-navy-400 hover:text-white transition-colors" data-id="element-2934">
              Kennisbank
            </a>
            <span className="text-navy-500" data-id="element-2935">/</span>
            <span className="text-accent-light font-medium" data-id="element-2936">
              WordPress onderhoud
            </span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-2937">
            WordPress onderhoud zonder zorgen
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-1" data-id="element-2938">
            Wij houden je WordPress website veilig, snel en up-to-date. Zodat
            jij je kunt focussen op ondernemen.
          </p>
          <div className="animate-fade-in-up-delay-2" data-id="element-2939">
            <a href="#pakketten" onClick={e => scrollToSection(e, 'pakketten')} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-2940">
              Bekijk onderhoudspakketten
              <ArrowRightIcon className="w-5 h-5" data-id="element-2941" />
            </a>
          </div>
        </div>
      </section>

      {/* Waarom onderhoud nodig is */}
      <section className="py-24 bg-surface-muted" data-id="element-2942">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2943">
          <div className="text-center mb-16" data-id="element-2944">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2945">
              Waarom WordPress onderhoud nodig is
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2946">
              Een WordPress website zonder onderhoud is kwetsbaar. Voorkom
              problemen met een professionele WordPress onderhoud service.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2947">
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2948">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2949">
                <ShieldCheckIcon className="w-6 h-6 text-accent" data-id="element-2950" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2951">
                Security updates
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2952">
                WordPress beveiligingsupdates beschermen je website tegen
                hackers en malware. Zonder tijdige updates loop je onnodig
                risico.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2953">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2954">
                <RefreshCwIcon className="w-6 h-6 text-accent" data-id="element-2955" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2956">
                Plugin updates
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2957">
                Verouderde plugins veroorzaken conflicten en beveiligingslekken.
                Wij houden al je plugins actueel en compatibel.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2958">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2959">
                <DatabaseIcon className="w-6 h-6 text-accent" data-id="element-2960" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2961">Backups</h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2962">
                Dagelijkse backups zorgen ervoor dat je website altijd te
                herstellen is, mocht er onverhoopt iets misgaan.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2963">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2964">
                <GaugeIcon className="w-6 h-6 text-accent" data-id="element-2965" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2966">
                Performance
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2967">
                Een snelle website scoort beter in Google en zorgt voor een
                betere gebruikerservaring. Wij optimaliseren continu.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Wat wij doen */}
      <section id="pakketten" className="py-24 bg-white" data-id="element-2968">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2969">
          <div className="text-center mb-16" data-id="element-2970">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2971">
              Wat wij doen
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2972">
              Ons WordPress onderhoud pakket dekt alles wat je nodig hebt om
              zorgeloos online te zijn.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2973">
            <div className="border border-navy-100 p-8 rounded-card flex items-start gap-5" data-id="element-2974">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-2975">
                <RefreshCwIcon className="w-6 h-6 text-navy-700" data-id="element-2976" />
              </div>
              <div data-id="element-2977">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2978">
                  WordPress updates
                </h3>
                <p className="text-slate-600" data-id="element-2979">
                  Wekelijkse controle en uitvoering van WordPress core, plugin
                  en thema updates. Altijd getest op compatibiliteit voordat we
                  live gaan.
                </p>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card flex items-start gap-5" data-id="element-2980">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-2981">
                <DownloadCloudIcon className="w-6 h-6 text-navy-700" data-id="element-2982" />
              </div>
              <div data-id="element-2983">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2984">
                  Backups
                </h3>
                <p className="text-slate-600" data-id="element-2985">
                  Dagelijkse automatische backups van je volledige website en
                  database. Extern opgeslagen zodat je data altijd veilig is.
                </p>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card flex items-start gap-5" data-id="element-2986">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-2987">
                <ActivityIcon className="w-6 h-6 text-navy-700" data-id="element-2988" />
              </div>
              <div data-id="element-2989">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2990">
                  Monitoring
                </h3>
                <p className="text-slate-600" data-id="element-2991">
                  24/7 uptime monitoring van je website. Bij downtime ontvangen
                  wij direct een melding en grijpen we in.
                </p>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card flex items-start gap-5" data-id="element-2992">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-2993">
                <BugIcon className="w-6 h-6 text-navy-700" data-id="element-2994" />
              </div>
              <div data-id="element-2995">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2996">
                  Technische support
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-2997">
                  Loop je ergens tegenaan? Wij helpen je snel weer op weg. Van
                  formulieren tot weergaveproblemen. Wij staan paraat.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center" data-id="element-2998">
            <a href="#contact" onClick={e => scrollToSection(e, 'contact')} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-semibold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg group" data-id="element-2999">
              Vraag een onderhoudspakket aan
              <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" data-id="element-3000" />
            </a>
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-surface-muted border-y border-navy-100" data-id="element-3001">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-3002">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-3003">
            Ontdek meer:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-3004">
            <a href="#/wordpress-websites" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-3005">
              WordPress websites
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-3006">•</span>
            <a href="#/hosting-onderhoud" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-3007">
              Website hosting
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-3008">•</span>
            <a href="#/website-laten-maken" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-3009">
              Website laten maken
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white" data-id="element-3010">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3011">
          <div className="text-center mb-16" data-id="element-3012">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-3013">
              Veelgestelde vragen over WordPress onderhoud
            </h2>
          </div>

          <div className="space-y-4" data-id="element-3014">
            {faqs.map((faq, index) => <div key={index} className="bg-surface-muted rounded-xl shadow-sm border border-navy-50 overflow-hidden" data-id="element-3015">
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-3016">
                  <span className="font-bold text-navy-900" data-id="element-3017">{faq.q}</span>
                  {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-3018" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-3019" />}
                </button>
                {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-3020">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-3021" />
    </main>;
}